import React from 'react';
import Head from 'next/head';
import NextSeo from 'next-seo';
import { NextPage, NextPageContext } from 'next';

import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

import { useTranslations } from '@lang';
import { RegisterVersion } from '@features/account/pages/Register/RegisterVersion';
import * as entityActions from '@store/entity/actions';
import { NormalizedStore } from '@store/entity/entity.types';

type RegisterProps = {
  language: string;
  property: Property;
  propertyTitle: string;
  socialFallbackImage: Image;
  url: string;
};

const Register: NextPage<RegisterProps> = ({
  language,
  property,
  propertyTitle,
  socialFallbackImage,
  url,
}) => {
  const { t } = useTranslations();
  const pageName = t('app.register');
  const pageTitle = propertyTitle ? `${pageName} | ${propertyTitle}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <RegisterVersion language={language} property={property} />
    </>
  );
};

type RegisterInitialProps = {
  language: string;
  property: Property;
  store: NormalizedStore;
  url: string;
} & NextPageContext;

Register.getInitialProps = async ({
  language,
  property,
  store,
  url,
}: RegisterInitialProps): Promise<RegisterProps> => {
  if (property.homepage) {
    await entityActions.fetchHomepage(property.id, language)(store.dispatch);
  }

  return {
    url,
    language,
    property,
    propertyTitle: property.propertyTitle || '',
    socialFallbackImage: property.socialFallbackImage,
  };
};

export default Register;
